import { useContext, useEffect, useRef, useState } from 'react'
import { NavigationStateContext } from '@opallabs/beacon/src/OpalNavigation/hooks/NavigationStateContext'
import { JumpSearchContext } from '@opallabs/beeblebrox/src/useJumpSearch'
import { useIsCopilotOpen } from '../CopilotLayoutProvider'
import { BUTTON_INSET, BUTTON_SIZE } from './styles'

type GemButtonState = Readonly<{
  blockInset: number
  blockSize: number
  inlineInset: number
  inlineSize: number
  isVisible: boolean
}>

const hiddenState = {
  blockInset: 0,
  blockSize: 0,
  inlineInset: 0,
  inlineSize: 0,
  isVisible: false,
} as const satisfies GemButtonState
const visibleState = {
  blockInset: BUTTON_INSET,
  blockSize: BUTTON_SIZE,
  inlineInset: BUTTON_INSET,
  inlineSize: BUTTON_SIZE,
  isVisible: true,
} as const satisfies GemButtonState

// Gem's panel slides out over 500ms (the `inset-inline-end` transition on
// Lapidary's `Sidebar.Content`), but it is off the button's corner well before
// it lands, and waiting for the full slide reads as the button being slow to
// return.
const PANEL_SLIDE_OUT_MS = 350

// True for as long as the panel is still on its way out. Only a real
// open-to-closed transition starts the wait: a page that never opened Gem has
// no panel to wait for, and would otherwise hold its button back on load.
const usePanelSlidingOut = (isCopilotOpen: boolean): boolean => {
  const wasOpen = useRef(isCopilotOpen)
  const [slidingOut, setSlidingOut] = useState(false)

  useEffect(() => {
    const justClosed = wasOpen.current && !isCopilotOpen
    wasOpen.current = isCopilotOpen
    if (!justClosed) return

    setSlidingOut(true)
    const timer = setTimeout(() => setSlidingOut(false), PANEL_SLIDE_OUT_MS)
    return () => clearTimeout(timer)
  }, [isCopilotOpen])

  return slidingOut
}

/**
 * Whether the Gem trigger button is on screen, and the size and offset (in pixels) it occupies.
 */
export const useGemButtonState = (): GemButtonState => {
  const isCopilotOpen = useIsCopilotOpen()
  const isJumpSearchOpen = useContext(JumpSearchContext)?.open ?? false
  const navigationHidden =
    useContext(NavigationStateContext)?.navigationHidden ?? false
  const editorFullscreen = false
  const panelSlidingOut = usePanelSlidingOut(isCopilotOpen)

  const isHidden =
    isCopilotOpen ||
    panelSlidingOut ||
    isJumpSearchOpen ||
    navigationHidden ||
    editorFullscreen
  return isHidden ? hiddenState : visibleState
}
